/**
 * Phase 14 — Per-day Buffet print buttons.
 *
 * Renders two compact buttons near the OVC section header in the day
 * editor: "Print Buffet Day (Detailed)" and "Print Buffet Day (Summary)".
 * Each button opens a small popover that lets the user pick paper size
 * (A4 or Letter) and buffet layout (Kitchen or Line) before triggering
 * the corresponding `/export/buffet-detailed-pdf` or
 * `/export/buffet-summary-pdf` download.
 *
 * The popover uses the existing Hotfix-2 selector pattern (small box,
 * 2 radio groups). Downloads happen by opening the PDF URL in a new
 * tab so the browser native viewer pops up and the user can save /
 * print directly.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Printer, FileText } from "lucide-react";

function PaperLayoutPicker({ paper, setPaper, layout, setLayout }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-700">
          Paper size
        </Label>
        <div className="mt-1.5 flex gap-2">
          {["a4", "letter"].map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPaper(p)}
              data-testid={`buffet-print-paper-${p}`}
              className={`flex-1 rounded-md border px-2 py-1.5 text-xs font-medium uppercase tracking-wide ${
                paper === p
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {p === "a4" ? "A4" : "Letter"}
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-700">
          Buffet layout
        </Label>
        <div className="mt-1.5 flex gap-2">
          {[
            { val: "kitchen", label: "By Kitchen" },
            { val: "line",    label: "By Line / Island" },
          ].map((opt) => (
            <button
              key={opt.val}
              type="button"
              onClick={() => setLayout(opt.val)}
              data-testid={`buffet-print-layout-${opt.val}`}
              className={`flex-1 rounded-md border px-2 py-1.5 text-[11px] font-medium ${
                layout === opt.val
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function BuffetPrintButton({ voyageId, dayNumber, kind, label, icon: Icon }) {
  const [open, setOpen] = useState(false);
  const [paper, setPaper] = useState("a4");
  const [layout, setLayout] = useState("kitchen");

  // Endpoint suffix per kind: 'detailed' or 'summary'.
  const endpoint =
    kind === "detailed" ? "buffet-detailed-pdf" : "buffet-summary-pdf";

  const open_pdf = () => {
    // Read the same token the apiClient uses so the PDF download goes
    // through the same access gate.
    let token = "";
    try {
      token = window.localStorage.getItem("access_token") || "";
    } catch {
      token = "";
    }
    const qs = new URLSearchParams({
      paper, buffet_layout: layout,
    });
    if (token) qs.set("token", token);
    const url = `${
      process.env.REACT_APP_BACKEND_URL || ""
    }/api/voyages/${voyageId}/days/${dayNumber}/export/${endpoint}?${qs.toString()}`;
    window.open(url, "_blank");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 gap-1.5 text-[11px]"
          data-testid={`buffet-print-${kind}-trigger`}
        >
          <Icon className="h-3.5 w-3.5" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72"
        data-testid={`buffet-print-${kind}-popover`}
      >
        <div className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-800">
          Print Buffet Day — {kind === "detailed" ? "Detailed" : "Summary"}
        </div>
        <PaperLayoutPicker
          paper={paper} setPaper={setPaper}
          layout={layout} setLayout={setLayout}
        />
        <div className="mt-4 flex justify-end">
          <Button
            size="sm"
            onClick={open_pdf}
            data-testid={`buffet-print-${kind}-go`}
          >
            <Printer className="h-3.5 w-3.5" />
            Open PDF
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function BuffetDayPrintButtons({ voyageId, dayNumber }) {
  return (
    <div className="flex flex-wrap items-center gap-2"
         data-testid="buffet-day-print-buttons">
      <BuffetPrintButton
        voyageId={voyageId}
        dayNumber={dayNumber}
        kind="detailed"
        label="Print Buffet Day (Detailed)"
        icon={FileText}
      />
      <BuffetPrintButton
        voyageId={voyageId}
        dayNumber={dayNumber}
        kind="summary"
        label="Print Buffet Day (Summary)"
        icon={Printer}
      />
    </div>
  );
}
